import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowRight, MessageCircle } from 'lucide-react'
import { selectEnquiryRole } from '@/lib/enquiry'
import Container from '@/components/landing/container'
import SectionHeading from '@/components/landing/section-heading'

import { HIRING_STEPS, LINKS, RECRUITER_BENEFITS } from '@/constants/landing'

const RecruitersSection: FC = (): ReactNode => {
    return (
        <section id='recruiters' aria-labelledby='recruiters-title' className='relative isolate overflow-hidden bg-brand-navy py-20 text-white supports-[overflow:clip]:overflow-clip sm:py-28'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <div className='absolute -right-32 -top-40 size-[36rem] rounded-full bg-brand-teal/20 blur-3xl' />
                <div className='absolute -bottom-48 -left-32 size-[30rem] rounded-full bg-primary/60 blur-3xl' />
                <div className='absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.07)_1px,transparent_0)] bg-[length:26px_26px]' />
            </div>

            <Container className='grid items-center gap-16 lg:grid-cols-12 lg:gap-14'>
                <div className='lg:col-span-6'>
                    <SectionHeading
                        id='recruiters-title'
                        tone='dark'
                        eyebrow="For recruiters"
                        title="Hire smarter. Retain longer."
                        description="Pre-screened, sector-trained candidates who are ready to perform from day one, sourced locally across tier 2 and tier 3 cities."
                    />

                    <ul className='mt-12 grid gap-x-8 gap-y-9 sm:grid-cols-2'>
                        {RECRUITER_BENEFITS.map(benefit => {
                            const Icon = benefit.icon

                            return (
                                <li key={benefit.title} className='reveal'>
                                    <span className='grid size-12 place-items-center rounded-2xl bg-white/10 text-brand-teal-bright ring-1 ring-white/15'>
                                        <Icon className='size-6' aria-hidden='true' />
                                    </span>

                                    <h3 className='mt-4 text-lg font-semibold text-white'>
                                        {benefit.title}
                                    </h3>

                                    <p className='mt-2 text-[15px] leading-relaxed text-white/70'>
                                        {benefit.description}
                                    </p>
                                </li>
                            )
                        })}
                    </ul>

                    <div className='mt-12 flex flex-col gap-3 sm:flex-row'>
                        <a
                            href='#contact'
                            onClick={() => selectEnquiryRole('recruiter')}
                            className='group inline-flex h-13 items-center justify-center gap-2 rounded-full bg-brand-orange px-7 font-semibold text-brand-navy transition-colors hover:bg-brand-orange/90'
                        >
                            Share your hiring needs
                            <ArrowRight className='size-5 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                        </a>

                        <a
                            href={LINKS.whatsappRecruiter}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='inline-flex h-13 items-center justify-center gap-2 rounded-full border border-white/25 px-7 font-semibold text-white transition-colors hover:bg-white/10'
                        >
                            <MessageCircle className='size-5' aria-hidden='true' />
                            Chat on WhatsApp
                            <span className='sr-only'>(opens in a new tab)</span>
                        </a>
                    </div>
                </div>

                <div className='reveal relative lg:col-span-6'>
                    <div className='relative aspect-[4/5] overflow-hidden rounded-[2rem] bg-primary ring-1 ring-white/10 sm:aspect-[4/3] lg:aspect-[4/5]'>
                        <Image
                            src='/images/talent-professional.png'
                            alt="Confident young professional in a blue shirt holding a laptop"
                            fill
                            sizes='(min-width: 1024px) 45vw, 100vw'
                            className='object-cover object-[50%_15%]'
                        />

                        <div aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-brand-navy/10 to-transparent' />
                    </div>

                    <div className='relative -mt-32 mx-4 rounded-2xl bg-white p-5 text-foreground shadow-2xl sm:mx-8 sm:p-7'>
                        <p className='text-sm font-semibold text-brand-teal-ink'>
                            How hiring with Skillkafe works
                        </p>

                        <ol className='mt-5 space-y-5'>
                            {HIRING_STEPS.map((step, index) => (
                                <li key={step.title} className='flex gap-4'>
                                    <span className='grid size-8 shrink-0 place-items-center rounded-full bg-primary font-heading text-sm font-semibold text-primary-foreground'>
                                        {index + 1}
                                    </span>

                                    <div>
                                        <p className='font-semibold leading-snug text-foreground'>
                                            {step.title}
                                        </p>

                                        <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>
                                            {step.description}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </Container>
        </section>
    )
}

export default RecruitersSection
